import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { CustomLoggerService } from '@monorepo/services';
import { TAppMaster } from '@monorepo/resolvers';
import {
  TBackUpDataStatusDB,
  TCacheResetTimestampDB,
  TImagesBucketDB,
  TUserActualsDB,
  TMasterdataListDB,
  TRESPONSE_BUSINESS_CONTEXT_ITEMS,
  TRESPONSE_MASTER_DATA,
  TRESPONSE_SHIFTS,
  SESSION_PLANS_DATA,
  TServerDefectivesDB,
  TImagesBucketMetadataDB,
  TServerImagesBucketDB,
  TBackgroundEventsSyncingDB,
  TRoamingQcDB,
  TDevicesDB,
  TBulkCountAppUserActualsDB,
  TRESPONSE_VendorsResponse,
  TBulkCountAppBackgroundDB,
  TBundleQcBgDB,
  TRemoteDeploymentDB,
  TAqlDB,
  TCutPanelInspectionDB,
  TRESPONSE_GET_CUTBANK_SUBJECTS,
  TPastDaySyncedEventsDB,
  TWipPackerStore,
  TMeasurementQcDB,
  TDailyModels,
} from '@monorepo/types';
import { TAppMasterDatabase } from '../redux-store/app-master-db/app-master-db.reducer';
import { Capacitor } from '@capacitor/core';
import {
  CapacitorSQLite,
  CapacitorSQLitePlugin,
  SQLiteConnection,
  capSQLiteVersionUpgrade,
  SQLiteDBConnection,
} from '@capacitor-community/sqlite';

// [IMPORTANT] DO NOT EXPORT
type TDatabases = {
  '[APP_MASTER]': TAppMaster;
  '[APP_MASTER][USER_SETTING]': TAppMasterDatabase['settingsInfo'];
  '[LOGIN][AUTHENTICATION_TOKEN]': string;
  '[SHIFT][RESPONSE]': TRESPONSE_SHIFTS;
  '[SESSION_PLANS][RESPONSE]': SESSION_PLANS_DATA;
  '[SHIFT][USER_SETTING]': TRESPONSE_SHIFTS[0]['id'];
  '[BUSINESS_CONTEXT_ITEMS][RESPONSE]': TRESPONSE_BUSINESS_CONTEXT_ITEMS;
  '[MATER_DATA][RESPONSE]': TRESPONSE_MASTER_DATA['response'];
  '[APP][MODEL]': TDailyModels[]; // TODO: not required check once MD is live
  '[APP][INPUT_APP_CLIENT_DB]': TUserActualsDB;
  '[APP][MASTER_DATA_LIST_DB]': TMasterdataListDB;
  '[APP][INPUT_APP_SERVER_DEFECTIVES_DB]': TServerDefectivesDB;
  '[APP][INPUT_APP_NEXT_CACHE_RESET_TIMESTAMP_DB]': TCacheResetTimestampDB;
  '[APP][INPUT_APP_BACK_UP_DATA_STATUS]': TBackUpDataStatusDB;
  '[APP][INPUT_APP_IMAGES_BUCKET_DB]': TImagesBucketDB;
  '[APP][INPUT_APP_IMAGES_BUCKET_METADATA_DB]': TImagesBucketMetadataDB;
  '[APP][INPUT_APP_SERVER_IMAGES_BUCKET_DB]': TServerImagesBucketDB;
  '[APP][INPUT_APP_BACKGROUND_EVENTS_SYNCING_DB]': TBackgroundEventsSyncingDB;
  '[APP][INPUT_APP_PAST_DAY_SYNCED_EVENTS_DB]': TPastDaySyncedEventsDB;
  '[APP][INPUT_APP_ROAMING_QC_DB]': TRoamingQcDB;
  '[APP][INPUT_APP_DEVICES_DB]': TDevicesDB;
  '[APP][INPUT_APP_BULK_COUNT_DB]': TBulkCountAppUserActualsDB;
  '[APP][INPUT_APP_BULK_COUNT_BACKGROUND_DB]': TBulkCountAppBackgroundDB;
  '[APP][INPUT_APP_BULK_COUNT_VENDORS_DB]': TRESPONSE_VendorsResponse;
  '[APP][INPUT_APP_BUNDLE_QC_BACKGROUND_DB]': TBundleQcBgDB;
  '[APP][INPUT_APP_UPDATES]': TRemoteDeploymentDB;
  '[APP][INPUT_APP_AQL_DB]': TAqlDB;
  '[APP][CUT_PANEL_INSPECTION_DB]': TCutPanelInspectionDB;
  '[APP][INPUT_APP_CUTBANK_SUBJECTS]': TRESPONSE_GET_CUTBANK_SUBJECTS;
  '[APP][INPUT_APP_CUTBANK_OUT_SUBJECTS]': TRESPONSE_GET_CUTBANK_SUBJECTS;
  '[APP][INPUT_APP_CUTBANK_IN_SUBJECTS]': TRESPONSE_GET_CUTBANK_SUBJECTS;
  '[APP][INPUT_APP_CUTBANK_IN_COMPONENT_TAG_ENABLED]': boolean;
  '[APP][INPUT_APP_CUTBANK_OUT_COMPONENT_TAG_ENABLED]': boolean;
  '[APP][INPUT_APP_WIP_PACKER_DB]': TWipPackerStore;
  '[APP][MEASUREMENT_QC_DB]': TMeasurementQcDB;
};
export type TStorageMode = 'SQLITE' | 'INDEXEDDB';
const FALLBACK_STORAGE_MODE: TStorageMode = 'SQLITE';

const SQLITE_DB_NAME = 'factory_database';

const SQLITE_DB_UPGRADE_STATEMENTS: capSQLiteVersionUpgrade[] = [
  {
    toVersion: 1,
    statements: [
      `
      CREATE TABLE IF NOT EXISTS factory_data(
      "key" TEXT PRIMARY KEY,
      "data" TEXT NOT NULL
      );
    `,
    ],
  },
];

@Injectable({
  providedIn: 'root',
})
export class PersistentStorageService {
  #storage?: Storage = undefined;
  #platform = Capacitor.getPlatform() as 'android' | 'ios' | 'web';
  #sqlitePlugin?: CapacitorSQLitePlugin = undefined;
  #sqliteConnection?: SQLiteConnection = undefined;
  #sqliteDB?: SQLiteDBConnection = undefined;
  #storageMode: TStorageMode | null = null;

  constructor(
    private storage: Storage,
    private customLoggerService: CustomLoggerService,
  ) {}

  async init() {
    this.#storage = await this.storage.create();
    if (this.#platform === 'web') {
      this.#logInfo('INIT_WEB', { driver: this.#storage?.driver ?? 'n/a' });
      this.#storageMode = 'INDEXEDDB';
      return;
    }
    this.#logInfo('INIT_SQLITE_PLUGIN');

    this.#sqlitePlugin = CapacitorSQLite;
    this.#sqliteConnection = new SQLiteConnection(this.#sqlitePlugin);

    await this.#sqlitePlugin.addUpgradeStatement({
      database: SQLITE_DB_NAME,
      upgrade: SQLITE_DB_UPGRADE_STATEMENTS,
    });
    this.#sqliteDB = await this.#openDatabase();

    this.#storageMode = await this.#resolveStorageModeFromStoredAppMaster();

    if (this.#storageMode === 'INDEXEDDB') {
      await this.#closeSqlite();
    } else if (this.#storageMode === 'SQLITE') {
      // close IndexedDB
    }

    this.#logInfo('INIT_COMPLETE', {
      storageMode: this.#storageMode ?? 'NO_APP_MASTER_AVAILABLE',
      driver: this.#storage?.driver ?? 'n/a',
      sqliteOpen: !!this.#sqliteDB,
    });
  }

  //   WEB
  // └── IndexedDB only

  // NATIVE
  // ├── IndexedDB initialized
  // ├── SQLite initialized
  // ├── Existing APP_MASTER?
  // │   ├── IndexedDB → close SQLite
  // │   ├── SQLite    → close IndexedDB (do nothing)
  // │   └── Not found → keep both alive
  // │
  // └── API returns APP_MASTER
  //     └── applyStorageModeFromAppMaster()
  //         ├── IndexedDB → close SQLite
  //         └── SQLite    → close IndexedDB (do nothing)

  // This method should be used only when the storage is initialized
  async applyStorageModeFromAppMaster(appMaster: TAppMaster | null | undefined) {
    if (this.#platform === 'web') return;

    const useIndexedDb = appMaster?.apps?.some((app) => app?.appInfo?.use_indexed_db_storage === true);

    this.#storageMode = useIndexedDb ? 'INDEXEDDB' : 'SQLITE';
    if (useIndexedDb) {
      await this.#closeSqlite();
    }

    this.#logInfo('STORAGE_MODE_SWITCHED', {
      storageMode: this.#storageMode,
      sqliteOpen: !!this.#sqliteDB,
    });
  }

  async #resolveStorageModeFromStoredAppMaster(): Promise<TStorageMode | null> {
    try {
      if (await this.#storage?.get('[APP_MASTER]')) {
        this.#logInfo('APP_MASTER_FOUND', { foundIn: 'INDEXEDDB' });
        return 'INDEXEDDB';
      }
      if (await this.#sqliteGet('[APP_MASTER]')) {
        this.#logInfo('APP_MASTER_FOUND', { foundIn: 'SQLITE' });
        return 'SQLITE';
      }
      this.#logInfo('APP_MASTER_ABSENT');
      return null;
    } catch (err) {
      this.#logStorageError('RESOLVE_STORAGE_MODE', '[APP_MASTER]', err);
      return null;
    }
  }

  async #closeSqlite() {
    try {
      await this.#sqliteDB?.close();
      if (this.#sqliteConnection && (await this.#sqliteConnection.isConnection(SQLITE_DB_NAME, false)).result) {
        await this.#sqliteConnection.closeConnection(SQLITE_DB_NAME, false);
      }
      this.#logInfo('SQLITE_CONNECTION_CLOSED');
    } catch (err) {
      this.#logStorageError('CLOSE_SQLITE', SQLITE_DB_NAME, err);
    } finally {
      this.#sqliteDB = undefined;
    }
  }
  #useSqlite(): boolean {
    return !!this.#sqliteDB && (this.#storageMode ?? FALLBACK_STORAGE_MODE) === 'SQLITE';
  }

  async get<T extends keyof TDatabases>(key: T): Promise<TDatabases[T]> {
    try {
      if (this.#useSqlite()) {
        return await this.#sqliteGet(key);
      } else return await this.#storage?.get(key);
    } catch (err) {
      console.log('[ERROR](persistent-storage-service) :', key, err);
      this.#logStorageError('GET', key, err);
      return null as unknown as TDatabases[T];
    }
  }

  async remove<T extends keyof TDatabases>(key: T): Promise<TDatabases[T]> {
    try {
      if (this.#useSqlite()) {
        const res = await this.get(key);
        await this.#sqliteDB?.run(`DELETE FROM factory_data WHERE "key" = ?`, [key]);
        return res;
      } else return await this.#storage?.remove(key);
    } catch (err) {
      console.log('[ERROR](persistent-storage-service) :', key, err);
      this.#logStorageError('REMOVE', key, err);
      return undefined as unknown as TDatabases[T];
    }
  }

  async set<T extends keyof TDatabases>(key: T, value: TDatabases[T]) {
    try {
      if (this.#useSqlite()) {
        await this.#sqliteSet(key, value);
        return value;
      } else return await this.#storage?.set(key, value);
    } catch (err) {
      console.log('[ERROR](persistent-storage-service):', key, err);
      this.#logStorageError('SET', key, err);
      return value;
    }
  }

  async clearExceptKeys<T extends keyof TDatabases>(toBePreservedKeys: T[]) {
    try {
      if (this.#useSqlite()) {
        if (!toBePreservedKeys.length) {
          await this.#sqliteDB?.run(`DELETE FROM factory_data`);
          return;
        }

        const placeholders = toBePreservedKeys.map(() => '?').join(', ');
        await this.#sqliteDB?.run(`DELETE FROM factory_data WHERE "key" NOT IN (${placeholders})`, toBePreservedKeys);
      } else if (this.#storage) {
        const allKeys = await this.#storage?.keys();
        const toBeRemovedKeys = allKeys.filter((key) => !toBePreservedKeys.includes(key as T));

        for (const key of toBeRemovedKeys) {
          await this.#storage?.remove(key);
        }
      }
    } catch (err) {
      console.log('[ERROR](persistent-storage-service) clearExceptKeys:', toBePreservedKeys, err);
      this.#logStorageError('CLEAR', toBePreservedKeys, err);
    }
  }

  async #sqliteGet(key: string) {
    const res = await this.#sqliteDB?.query(`SELECT data FROM factory_data WHERE "key" = ?`, [key]);
    return res?.values?.length ? JSON.parse(res.values[0].data) : null;
  }

  async #sqliteSet(key: string, value: unknown) {
    await this.#sqliteDB?.run(
      `
            INSERT INTO factory_data ("key", "data")
            VALUES (?, ?)
            ON CONFLICT("key")
            DO UPDATE SET "data" = excluded."data";
          `,
      [key, JSON.stringify(value)],
    );
  }

  async #openDatabase() {
    if (!this.#sqliteConnection || this.#platform === 'web') return;

    const version = SQLITE_DB_UPGRADE_STATEMENTS[SQLITE_DB_UPGRADE_STATEMENTS.length - 1].toVersion;

    const isConsistent = (await this.#sqliteConnection.checkConnectionsConsistency()).result;
    const isConnected = (await this.#sqliteConnection.isConnection(SQLITE_DB_NAME, false)).result;

    const db: SQLiteDBConnection =
      isConnected && isConsistent
        ? await this.#sqliteConnection.retrieveConnection(SQLITE_DB_NAME, false)
        : await this.#sqliteConnection.createConnection(SQLITE_DB_NAME, false, 'no-encryption', version, false);

    await db.open();
    return db;
  }

  #logStorageError(operation: string, dbKey: any, err: unknown) {
    this.customLoggerService.log('#PERSISTENT_STORAGE_SERVICE', `PERSISTENT_STORAGE_${operation}_FAILED`, {
      key: dbKey,
      platform: this.#platform,
      error: err instanceof Error ? { name: err.name, message: err.message, stack: err.stack } : String(err),
    });
  }

  #logInfo(event: string, payload: Record<string, unknown> = {}) {
    this.customLoggerService.log('#PERSISTENT_STORAGE_SERVICE', event, {
      platform: this.#platform,
      ...payload,
    });
  }
}
